import React, { useState } from 'react';
import { uploadFile } from '../utils/dataService';

const FileUploader = ({ folderPath, onFileUploaded, acceptedTypes = '*', maxSize = 50 }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  
  // Convert maxSize from MB to bytes
  const maxSizeBytes = maxSize * 1024 * 1024;
  
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setError('');
    
    if (!selectedFile) {
      setFile(null);
      return;
    }
    
    // Check file size
    if (selectedFile.size > maxSizeBytes) {
      setError(`File is too large. Maximum size is ${maxSize}MB.`);
      setFile(null);
      return;
    }
    
    // Check file type if specific types are required
    if (acceptedTypes !== '*') {
      const fileType = selectedFile.type;
      const acceptedTypesArray = acceptedTypes.split(',');
      
      if (!acceptedTypesArray.some(type => fileType.match(type.trim()))) {
        setError(`Invalid file type. Accepted types: ${acceptedTypes}`);
        setFile(null);
        return;
      }
    }
    
    setFile(selectedFile);
  };
  
  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    if (typeof folderPath !== 'string' || folderPath.trim() === '') {
      setError('Invalid folder path. Please provide a valid folder path.');
      return;
    }

    try {
      setUploading(true);
      setProgress(0);
      
      // Simulate progress (in real implementation, you'd use Firebase's upload progress)
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 300);
      
      // Upload file
      const fileData = await uploadFile(file, `${folderPath}/${file.name}`);
      
      // Set progress to 100% when done
      clearInterval(progressInterval);
      setProgress(100);
      
      // Clear file and notify parent component
      setTimeout(() => {
        setUploading(false);
        setProgress(0);
        setFile(null);
        if (onFileUploaded) {
          onFileUploaded(fileData);
        }
      }, 500);
      
    } catch (error) {
      setError(`Upload failed: ${error.message}`);
      setUploading(false);
      setProgress(0);
    }
  };
  
  return (
    <div className="mb-6">
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
        <input
          type="file"
          onChange={handleFileChange}
          className="hidden"
          id="file-upload"
          accept={acceptedTypes}
          disabled={uploading}
        />
        
        {!file && !uploading && (
          <div>
            <label
              htmlFor="file-upload"
              className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded inline-block"
            >
              Select File
            </label>
            <p className="mt-2 text-sm text-gray-500">
              {acceptedTypes === '*' 
                ? `Drag a file here or click to select (max ${maxSize}MB)`
                : `Accepted file types: ${acceptedTypes} (max ${maxSize}MB)`}
            </p>
          </div>
        )}
        
        {file && !uploading && (
          <div>
            <p className="mb-2">
              <span className="font-semibold">Selected file:</span> {file.name}
            </p>
            <div className="flex space-x-4 justify-center">
              <button
                onClick={() => setFile(null)}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
              >
                Upload File
              </button>
            </div>
          </div>
        )}
        
        {uploading && (
          <div>
            <p className="mb-2">Uploading: {file.name}</p>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
              <div 
                className="bg-blue-600 h-2.5 rounded-full" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-500">{progress}% Complete</p>
          </div>
        )}
        
        {error && (
          <p className="mt-2 text-sm text-red-600">{error}</p>
        )}
      </div>
    </div>
  );
};

export default FileUploader;